// Pure helpers for analytics-v2 responses. Kept free of app path aliases so
// the repo-root vitest project can import them directly.

import { z } from 'zod'

type PlaceholderCheckResponse =
  | {
      rows: ReadonlyArray<unknown>
      transparency: { measureUsed: string }
    }
  | null
  | undefined

/**
 * True when the bridge answered with a placeholder instead of data: the
 * stale-if-slow cache mode returns an empty payload (empty rows AND an empty
 * transparency.measureUsed) while the underlying BigQuery run is still cold.
 * Callers may poll such responses; real empty results carry the measure name.
 */
export function isColdCachePlaceholder(response: PlaceholderCheckResponse): boolean {
  if (!response) {
    return false
  }
  return response.rows.length === 0 && response.transparency.measureUsed === ''
}

export const ANALYTICS_V2_TRANSIENT_RETRY_COUNT = 2
const ANALYTICS_V2_RETRY_BASE_DELAY_MS = 1_000
const ANALYTICS_V2_RETRY_MAX_BACKOFF_MS = 4_000
const ANALYTICS_V2_RETRY_JITTER_MS = 1_000

const CIRCUIT_BREAKER_ERROR_CODE = 'CIRCUIT_OPEN'
const TRANSIENT_HTTP_STATUS_FLOOR = 500

// The only two failures the reporting service answers with an opaque
// correlation id: Cube/BigQuery refused or outran the 25s continue-wait budget.
// Every permanent refusal it raises instead names its own cause verbatim
// ("Time range exceeds the 366-day limit", "Export cubes are not available
// through the analytics query API", "Classic DashView measures cannot be
// mixed...", "Staff access required"), so matching these two is an allow-list:
// an unrecognised message is never retried.
const TRANSIENT_SERVER_MESSAGE_PATTERN = /Query (?:failed|timed out)\. Reference: /

const transientErrorShapeSchema = z.object({
  code: z.string().optional(),
  issues: z.array(z.unknown()).optional(),
  response: z
    .object({
      status: z.number().optional(),
      errors: z.array(z.object({ message: z.string().optional() })).optional(),
    })
    .optional(),
})

/**
 * True when a failed analytics-v2 query is worth re-sending.
 *
 * Deliberately an allow-list. HTTP status is not a usable signal on its own:
 * the reporting service answers HTTP 200 with the failure inside the GraphQL
 * body, and because it throws `RequestException` (not `GraphQLError`) Apollo
 * collapses a 400 bad-query, a 403, a 429 and a 504 warehouse timeout to the
 * same `extensions.code` of INTERNAL_SERVER_ERROR. The message is the only
 * field that separates them.
 */
export function isTransientAnalyticsV2Error(error: unknown): boolean {
  const parsed = transientErrorShapeSchema.safeParse(error)
  if (!parsed.success) {
    return false
  }
  const { code, issues, response } = parsed.data

  // `normaliseResponse` parses the payload inside the query function, so a
  // schema drift surfaces here as a ZodError rather than as a request failure.
  // It has no `response` and would otherwise read as a network blip.
  if (issues !== undefined) {
    return false
  }

  // The shared client's circuit breaker holds for 30s after three failures and
  // answers without calling the network. That is longer than any budget a card
  // can spend retrying, so honouring it fails fast instead of spinning out the
  // full backoff only to surface the same tile.
  if (code === CIRCUIT_BREAKER_ERROR_CODE) {
    return false
  }

  // fetch itself rejected, so there was no HTTP exchange to classify: dropped
  // connection, DNS, TLS, Cloud Run still starting.
  if (response === undefined) {
    return true
  }

  const { status, errors } = response
  if (status !== undefined && status >= TRANSIENT_HTTP_STATUS_FLOOR) {
    return true
  }

  if (errors === undefined) {
    return false
  }

  return errors.some((graphQLError) => {
    const { message } = graphQLError
    if (message === undefined) {
      return false
    }
    return TRANSIENT_SERVER_MESSAGE_PATTERN.test(message)
  })
}

/**
 * Exponential backoff with additive jitter. `failureCount` is react-query's own
 * counter, which is 0-based at the point `retryDelay` is called.
 *
 * The jitter is load-bearing rather than decorative: the card that opts into
 * this fires thirty queries at once, so a transient fault fails all thirty
 * within a few ms of each other. A pure `base * 2^n` delay would re-send all
 * thirty in the same millisecond — a synchronised burst at a service that has
 * just shown it is struggling, and thirty simultaneous re-entries into Cube's
 * server-side continue-wait hold.
 */
export function getAnalyticsV2RetryDelayMs(failureCount: number, jitterFraction: number): number {
  const backoffMs = Math.min(
    ANALYTICS_V2_RETRY_BASE_DELAY_MS * 2 ** failureCount,
    ANALYTICS_V2_RETRY_MAX_BACKOFF_MS
  )
  return backoffMs + Math.round(jitterFraction * ANALYTICS_V2_RETRY_JITTER_MS)
}
